<template>
  <v-overlay v-if="loading" :value="loading" z-index="4">
    <v-progress-circular indeterminate size="64" />
  </v-overlay>
  <v-app v-else :style="cssProps">
    <div class="v-application--wrap">
      <app-drawer />
      <router-view />
    </div>
    <div
      class="refresh-container"
      v-if="hashChanged && $root.env !== 'development'"
    >
      <div class="notification-body">
        <div class="notification-button">
          <p class="text-center">
            An update is available. Please save all current work and click
            update below or just refresh your browser.
          </p>
          <p class="text-center">Not updating may result in errors.</p>
        </div>
        <div class="refresh-button text-center">
          <v-btn elevation="0" color="accent" rounded @click="reloadApp"
            >Update</v-btn
          >
        </div>
      </div>
    </div>
    <global-preview-media-dialog />
    <global-upload-dialog />
    <v-dialog
      v-model="showBrowserNotificationsDialog"
      content-class="push-browser-dialog"
      hide-overlay
      max-width="300"
    >
      <v-card>
        <v-card-title class="">Allow push notifications?</v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            class="rounded-pill"
            text
            small
            @click="closeNotificationsAllowDialog"
            >Deny</v-btn
          >
          <v-btn
            class="rounded-pill elevation-0"
            small
            color="accent"
            @click="triggerBrowserNotificationsSettings"
            >Allow</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-expand-transition>
      <v-card
        elevation="0"
        class="tasks-container"
        v-show="computedTasks.length > 0"
      >
        <div class="tasks-header">
          <span class="tasks-header-title">Tasks</span>
          <v-progress-circular
            v-if="taskInProgress"
            size="16"
            indeterminate
            color="bhWhite"
            :width="2"
          ></v-progress-circular>
          <v-btn
            v-if="tasksMinimized"
            @click="maximizeTasks()"
            icon
            text
            small
            color="bhWhite"
            class="tasks-header-close"
            ><v-icon size="16">bhi-chevron-up</v-icon></v-btn
          >
          <v-btn
            v-else
            @click="minimizeTasks()"
            icon
            text
            small
            color="bhWhite"
            class="tasks-header-close"
            ><v-icon size="16">bhi-chevron-down</v-icon></v-btn
          >
        </div>
        <v-expand-transition>
          <v-card elevation="0" v-show="!tasksMinimized">
            <v-expand-transition>
              <v-card elevation="0" v-show="taskInProgress" class="tasks-status"
                >work in progress...
              </v-card>
            </v-expand-transition>
            <div class="tasks-list">
              <div
                class="tasks-list--item"
                :class="task.status"
                :key="task.id"
                v-for="(task, index) in computedTasks"
              >
                <div class="tasks-list--item-overview">
                  <v-icon size="16">bhi-icon-spreadsheet</v-icon>
                  <span class="tasks-list--item-name">{{
                    task.type == 'participants_import'
                      ? 'Participants import'
                      : ''
                  }}</span>
                  <span class="tasks-list--item-status">{{
                    taskStatuses[task.status] ? taskStatuses[task.status] : ''
                  }}</span>
                  <v-btn
                    small
                    icon
                    text
                    color="accent"
                    v-if="task.status == 'completed'"
                    @click="deleteTask(task.id)"
                  >
                    <span>OK</span>
                  </v-btn>
                  <v-btn
                    small
                    icon
                    text
                    color="accent"
                    v-else-if="task.status == 'in_progress'"
                  >
                    <v-progress-circular
                      size="16"
                      indeterminate
                      color="bhBlue"
                      :width="2"
                    ></v-progress-circular>
                  </v-btn>
                  <v-btn
                    small
                    icon
                    text
                    color="accent"
                    v-else-if="task.status == 'failed'"
                    @click="toggleDetails(index)"
                  >
                    <v-icon size="16" color="bhRedDark">bhi-alert</v-icon>
                  </v-btn>
                  <v-btn
                    v-else-if="task.status == 'partially_completed'"
                    small
                    icon
                    text
                    color="accent"
                    @click="toggleDetails(index)"
                  >
                    <v-icon size="16" color="bhRedDark">bhi-alert</v-icon>
                  </v-btn>
                </div>
                <v-expand-transition>
                  <v-card
                    elevation="0"
                    class="tasks-list--item-details"
                    :class="task.status"
                    v-if="
                      (task.rows && task.rows.length > 0) ||
                        (task.taskErrors && task.taskErrors.length > 0)
                    "
                    v-show="!task.detailsCollapsed"
                  >
                    <ul v-if="task.taskErrors && task.taskErrors.length > 0">
                      <li
                        v-for="(error, index) in task.taskErrors"
                        :key="index"
                      >
                        {{ error }}
                      </li>
                    </ul>
                    <span v-if="task.rows && task.rows.length > 0"
                      >Please check following rows for errors in your
                      file:</span
                    >
                    <div class="tasks-list--item-details-list">
                      <div
                        class="tasks-list--item-detail"
                        v-for="row in task.rows"
                        :key="row.index"
                      >
                        Row {{ row.index }}:
                        <span v-for="val in row.values" :key="val">{{
                          errorLabels[val] ? errorLabels[val] : val
                        }}</span>
                      </div>
                    </div>
                    <v-btn
                      accent
                      small
                      rounded
                      color="bhRedDark"
                      class="ml-auto mr-0 d-block"
                      text
                      @click="deleteTask(task.id)"
                      >Delete from list</v-btn
                    >
                  </v-card>
                </v-expand-transition>
              </div>
            </div>
          </v-card>
        </v-expand-transition>
      </v-card>
    </v-expand-transition>
  </v-app>
</template>

<script>
import jwt_decode from 'jwt-decode'
import { mapActions, mapGetters, mapState, mapMutations } from 'vuex'
import { refreshPageMixin } from '@/mixins/refresh'
import appStates from '@/store/module-app-states'
import { debounce } from '@/helpers/debounce'
// load default config and extend it with tenant specific changes
import extendedConfig from './tenants.js'
import GlobalPreviewMediaDialog from '@/components/dialogs/GlobalPreviewMediaDialog.vue'
import GlobalUploadDialog from '@/components/dialogs/GlobalUploadDialog.vue'
import AppDrawer from '@/components/layout/AppDrawer.vue'

export default {
  name: 'App',
  components: {
    AppDrawer,
    GlobalPreviewMediaDialog,
    GlobalUploadDialog,
  },
  data() {
    return {
      loading: true,
      loaded: false,
      taskStatuses: {
        in_progress: 'In progress',
        completed: 'Success',
        failed: 'Failed',
        partially_completed: 'Completed, check errors',
      },
      errorLabels: {
        INTERNAL_ERROR: 'internal error',
        IMPORT_OVERLOADED: 'import limit reached',
        DUPLICATED_EMAIL: 'e-mail address already used',
        INVALID_SCHEMA: 'wrong column name',
        INVALID_EMAIL: 'e-mail address is invalid',
        MISSING_LASTNAME: 'last name is missing',
        MISSING_FIRSTNAME: 'first name is missing',
        MISSING_EMAIL: 'e-mail address is missing',
      },
      computedTasks: [],
      getTasksTimeout: null,
      tasksMinimized: false,
      showBrowserNotificationsDialog: false,
    }
  },
  mixins: [refreshPageMixin],
  watch: {
    $route: {
      immediate: true,
      handler(to, from) {
        const newTitle = to?.meta?.title
          ? `${to.meta.title} - ${to?.meta?.rootTitle ||
              extendedConfig.VUE_MAIN_TITLE}`
          : `${to?.meta?.rootTitle || extendedConfig.VUE_MAIN_TITLE}`

        document.title = newTitle

        if (to?.meta?.favicon) {
          let faviconLink = document.querySelector("link[rel~='icon']")
          if (!faviconLink) {
            faviconLink = document.createElement('link')
            faviconLink.rel = 'icon'
            document.head.appendChild(faviconLink)
          }
          faviconLink.href = to.meta.favicon
        }

        const metas = Array.from(document.getElementsByTagName('meta'))
        const metaTitle = metas.find(
          m => m.attributes[0].nodeValue === 'og:title',
        )
        const metaUrl = metas.find(m => m.attributes[0].nodeValue === 'og:url')
        if (metaTitle) {
          metaTitle.attributes[1].nodeValue = newTitle
        }
        if (metaUrl) {
          metaUrl.attributes[1].nodeValue =
            window.location.origin + window.location.pathname
        }
      },
    },
    tasks(val) {
      if (val) this.computeTasks()
    },
    async loggedIn(val) {
      if (val && !this.loaded) {
        this.loading = true
        await this.collectRequiredData()

        if (!this.profileId) {
          await this.getProfileData()
        }
        if (this.isAdvisor) {
          this.getNotificationsGrouped()
        } else {
          this.getNotifications({
            isAdvisor: this.isAdvisor,
          })
        }
        this.checkNotificationAllowedDebounced()
        this.loaded = true
        this.loading = false
      } else {
        this.loaded = false
      }
    },
  },
  async mounted() {
    this.$store.commit('userStates/updateUser', {})
    this.setProfilePreviewsQueue([])
    this.initVersionCheck(extendedConfig.BASE_URL + 'version.json')
    if (this.getToken && this.viewRequiresAuth) {
      this.loading = true
      let that = this
      await this.collectRequiredData().then(() => {
        this.computeTasks()
        if (this.isAdvisor) {
          this.getNotificationsGrouped()
        } else {
          this.getNotifications({
            isAdvisor: this.isAdvisor,
          })
        }
        this.checkNotificationAllowedDebounced()
      })

      if (
        this.username &&
        Object.keys(this.user).length === 0 &&
        this.user.constructor === Object
      ) {
        await this.getProfileData().then(() => {
          this.computeTasks()
          if (this.isAdvisor) {
            this.getNotificationsGrouped()
          } else {
            this.getNotifications({
              isAdvisor: this.isAdvisor,
            })
          }
          this.checkNotificationAllowedDebounced()
        })
      }

      this.loaded = true
    }

    this.loading = false
  },
  computed: {
    ...mapState('dictionariesStates', [
      'dictionariesLoaded',
      'dictionaryFeatureFlagState',
    ]),
    ...mapState('userStates', ['loggedIn', 'tasks', 'browserNotifications']),
    ...mapState('appStates', ['viewRequiresAuth']),
    ...mapGetters({
      getToken: 'userStates/getToken',
    }),
    isAdvisor() {
      return this.rolename === 'Advisor'
    },
    username: {
      get() {
        return this.$store.state.userStates.username
      },
      set(val) {
        this.$store.commit('userStates/updateUsername', val)
      },
    },
    rolename: {
      get() {
        return this.$store.state.userStates.rolename
      },
      set(val) {
        this.$store.commit('userStates/updateRolename', val)
      },
    },
    mainFolderId: {
      get() {
        return this.$store.state.userStates.mainFolderId
      },
      set(val) {
        this.$store.commit('userStates/updateMainFolderId', val)
      },
    },
    userId: {
      get() {
        return this.$store.state.userStates.userId
      },
      set(val) {
        this.$store.commit('userStates/updateUserId', val)
      },
    },
    user: {
      get() {
        return this.$store.state.userStates.user
      },
      set(val) {
        this.$store.commit('userStates/updateUser', val)
      },
    },
    profileId: {
      get() {
        return this.$store.state.userStates.profileId
      },
      set(val) {
        this.$store.commit('userStates/updateProfileId', val)
      },
    },
    surrogateLogin: {
      get() {
        return this.$store.state.userStates.surrogateLogin
      },
      set(val) {
        this.$store.commit('userStates/updateSurrogateLogin', val)
      },
    },
    userTenant: {
      get() {
        return this.$store.state.userStates.userTenant
      },
      set(val) {
        this.$store.commit('userStates/updateUserTenant', val)
      },
    },
    userFeatureFlags: {
      get() {
        return this.$store.state.userStates.userFeatureFlags
      },
      set(val) {
        if (val) {
          const featureFlagState = Object.keys(this.dictionaryFeatureFlagState)
          if (val.surveyAvailable)
            val.surveyAvailable =
              featureFlagState.find(
                flagState =>
                  flagState.toLowerCase() === val.surveyAvailable.toLowerCase(),
              ) || val.surveyAvailable
        }
        this.$store.commit('userStates/updateUserFeatureFlags', { ...val })
      },
    },
    taskInProgress() {
      return this.computedTasks &&
        this.computedTasks.findIndex(task => task.status == 'in_progress') != -1
        ? true
        : false
    },
    cssProps() {
      return {
        '--main-color': extendedConfig.VUE_CSS_MAIN_COLOR || '#213665',
        '--bh-blue': extendedConfig.VUE_CSS_BH_BLUE || '#00a0e0',
        '--bh-blue--1': extendedConfig.VUE_CSS_BH_BLUE__1 || '#2bb2e9',
        '--bh-blue--2': extendedConfig.VUE_CSS_BH_BLUE__2 || '#45c2f4',
        '--bh-blue--3': extendedConfig.VUE_CSS_BH_BLUE__3 || '#bdd0ff',
        '--bh-blue--4': extendedConfig.VUE_CSS_BH_BLUE__4 || '#ebf0ff',
        '--bh-blue--5': extendedConfig.VUE_CSS_BH_BLUE__5 || '#eef2fe',
        '--bh-black': extendedConfig.VUE_CSS_BH_BLACK || '#25282b',
        '--bh-gray': extendedConfig.VUE_CSS_BH_GRAY || '#52575c',
        '--bh-gray--1': extendedConfig.VUE_CSS_BH_GRAY__1 || '#52575c',
        '--bh-gray--2': extendedConfig.VUE_CSS_BH_GRAY__2 || '#a0a4a8',
        '--bh-gray--3': extendedConfig.VUE_CSS_BH_GRAY__3 || '#cacccf',
        '--bh-gray--4': extendedConfig.VUE_CSS_BH_GRAY__4 || '#dbdde0',
        '--bh-gray--5': extendedConfig.VUE_CSS_BH_GRAY__5 || '#e8e8e8',
        '--bh-gray--6': extendedConfig.VUE_CSS_BH_GRAY__6 || '#f6f8fb',
        '--bh-new-gray': extendedConfig.VUE_CSS_BH_NEW_GRAY || '#f6f6fa',
        '--bh-white': extendedConfig.VUE_CSS_BH_WHITE || '#ffffff',
        '--bh-yellow': extendedConfig.VUE_CSS_BH_YELLOW || '#fac032',
        '--bh-yellow--dark':
          extendedConfig.VUE_CSS_BH_YELLOW__DARK || '#efad0a',
        '--bh-yellow--light':
          extendedConfig.VUE_CSS_BH_YELLOW__LIGHT || '#fcdf98',
        '--bh-green': extendedConfig.VUE_CSS_BH_GREEN || '#16d090',
        '--bh-green--dark': extendedConfig.VUE_CSS_BH_GREEN__DARK || '#03b575',
        '--bh-green--light':
          extendedConfig.VUE_CSS_BH_GREEN__LIGHT || '#34eeae',
        '--bh-red': extendedConfig.VUE_CSS_BH_RED || '#ff6760',
        '--bh-red--dark': extendedConfig.VUE_CSS_BH_RED__DARK || '#e74f48',
        '--bh-red--light': extendedConfig.VUE_CSS_BH_RED__LIGHT || '#ff7e77',
      }
    },
  },
  methods: {
    ...mapActions('dictionariesStates', ['getDictionaries']),
    ...mapActions('userStates', ['getNotificationsGrouped']),
    ...mapActions('chatStates', ['initChatService', 'getChatChannels']),
    ...mapActions('userStates', [
      'getProfile',
      'getTasks',
      'deleteTask',
      'getNotifications',
    ]),
    ...mapMutations('userStates', ['setBrowserNotifications']),
    ...mapMutations('profilePreviewsStates', ['setProfilePreviewsQueue']),
    closeNotificationsAllowDialog() {
      this.showBrowserNotificationsDialog = false
      this.setBrowserNotifications(false)
    },
    triggerBrowserNotificationsSettings() {
      let that = this
      that.showBrowserNotificationsDialog = false
      Notification.requestPermission(function() {
        if (Notification.permission !== 'granted') {
          alert(
            'Push notifications blocked. Change your notification and browser settings if you wish to bring them back.',
          )
          that.setBrowserNotifications(false)
          return
        }
      })
    },
    async checkNotificationAllowedDebounced() {
      debounce(
        () => this.checkNotificationAllowed(),
        2000,
        'checkNotificationAllowed',
      )
    },
    checkNotificationAllowed() {
      let that = this
      if (this.browserNotifications) {
        if (!window.Notification) {
          console.log('notifications not available')
          this.setBrowserNotifications(false)
        } else {
          if (Notification.permission !== 'granted')
            this.showBrowserNotificationsDialog = true
        }
      }
    },
    async collectRequiredData() {
      const decodedToken = jwt_decode(this.getToken) || {}
      const {
        uuid,
        sub,
        role_name,
        main_folder_id,
        user_id,
        surrogate_login,
        tenant,
        survey_available,
      } = decodedToken

      if (!this.dictionariesLoaded) {
        await this.getDictionaries()
      }

      if (uuid) {
        await this.getChatChannels()
        this.initChatService(uuid)
      }
      if (sub) {
        this.username = sub
      }
      if (role_name) {
        this.rolename = role_name
      }
      if (main_folder_id) {
        this.mainFolderId = main_folder_id
      }
      if (user_id) {
        this.userId = user_id
      }
      if (surrogate_login) {
        this.surrogateLogin = surrogate_login
      }
      if (tenant) {
        this.userTenant = tenant
      }

      /* Set up feature flags */
      const featureFlags = {
        surveyAvailable: 'Inactive',
      }
      if (survey_available) {
        featureFlags.surveyAvailable = survey_available
      }

      this.userFeatureFlags = { ...featureFlags }
    },
    async getProfileData() {
      this.profileId = jwt_decode(this.getToken).profile_id
      await this.getProfile({ profileId: this.profileId })
      await this.getTasks().then(() => {
        this.computeTasks()
      })
    },
    computeTasks() {
      if (this.tasks && this.tasks.length > 0) {
        let tmptasks = JSON.parse(JSON.stringify(this.tasks)) //deep clone, so the state is not affected

        // compare with previous state of tasks, if new added - maximize tasks modal window
        if (tmptasks.length != this.computedTasks.length) {
          this.tasksMinimized = false
        }

        let retry = false

        tmptasks.filter(task => {
          if (
            task.status == 'failed' &&
            task.metadata &&
            task.metadata.IMPORTED_PARTICIPANTS &&
            task.metadata.IMPORTED_PARTICIPANTS != '0'
          ) {
            task.status = 'partially_completed'
            task.detailsCollapsed = 1
          } else if (task.status == 'failed') {
            task.detailsCollapsed = 1
          }
          // if task status changed since last check
          if (
            this.computedTasks.find(
              oldTask => oldTask.id == task.id && oldTask.status != task.status,
            )
          )
            this.tasksMinimized = false

          let masterErrors = []
          let internalError = false
          if (task.errorDetails && task.errorDetails.length > 0) {
            let rows = []
            task.errorDetails.forEach(detail => {
              let row = false
              let email = false
              if (detail.value.indexOf('ROW_') > -1) {
                row = detail.value.split('ROW_').pop()
                if (row.indexOf('_') > -1) {
                  email = row
                    .split('_')
                    .slice(1)
                    .join('_')
                  row = row.split('_', 1)[0]
                }
              }
              if (row) {
                const ind = rows.findIndex(el => el.index == row)
                let label = detail.code
                if (detail.code == 'DUPLICATED_EMAIL') {
                  if (email) label = 'e-mail ' + email + ' is already in use'
                }
                if (ind == -1) {
                  rows.push({ index: row, values: [label] })
                } else {
                  rows[ind].values.push(label)
                }
              }

              switch (detail.code) {
                case 'INTERNAL_ERROR':
                  internalError = true
                  break
                case 'IMPORT_OVERLOADED':
                  if (
                    row &&
                    (!task.firstOverloadedRow || task.firstOverloadedRow > row)
                  ) {
                    task.firstOverloadedRow = row
                  }
                  break
                case 'DUPLICATED_EMAIL':
                  break
                case 'INVALID_SCHEMA':
                  masterErrors.push('Wrong column ' + detail.value + '.')
                  break
                case 'INVALID_EMAIL':
                  break
                case 'MISSING_LASTNAME':
                  break
                case 'MISSING_FIRSTNAME':
                  break
                case 'MISSING_EMAIL':
                  break
                default:
                  break
              }
            })
            task.rows = rows
          }
          if (task.firstOverloadedRow)
            masterErrors.push(
              'Import limit reached on row ' +
                task.firstOverloadedRow +
                ', ' +
                task.metadata.IMPORTED_PARTICIPANTS +
                ' participants were imported.',
            )
          if (internalError)
            masterErrors.push(
              'Internal error, please contact system administator.',
            )
          task.taskErrors = masterErrors
          if (task.status == 'in_progress') retry = true

          return task
        })
        if (retry) {
          clearTimeout(this.getTasksTimeout)
          this.getTasksTimeout = setTimeout(() => {
            this.getTasks()
          }, 5000)
        } else {
          clearTimeout(this.getTasksTimeout)
        }
        this.computedTasks = tmptasks
      } else {
        this.computedTasks = []
      }
    },
    toggleDetails(index) {
      this.computedTasks[index].detailsCollapsed = !this.computedTasks[index]
        .detailsCollapsed
    },
    minimizeTasks() {
      this.tasksMinimized = true
    },
    maximizeTasks() {
      this.tasksMinimized = false
    },
  },
}
</script>
<style lang="scss">
@import '@/styles/styles.scss';

.refresh-container {
  min-width: 200px;
  width: 15%;
  position: fixed;
  bottom: 10px;
  left: 15px;
  background-color: $bh-white;
  padding: 25px;
  z-index: 10;
  -webkit-box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3), 0 0 40px rgba(0, 0, 0, 0.05);
  -moz-box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3), 0 0 40px rgba(0, 0, 0, 0.05);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.3), 0 0 40px rgba(0, 0, 0, 0.05);
}
.close-refresh-modal {
  position: absolute;
  right: 5px;
  top: 5px;
  border: none;
}
.push-browser-dialog {
  position: absolute;
  top: 0;
  left: 0;
}
.tasks {
  &-container {
    width: 400px;
    position: fixed !important;
    bottom: 10px;
    left: 15px;
    background-color: $bh-white;
    z-index: 9;
    box-shadow: 0px 8px 24px 0px #0000000a;
    border-radius: 8px;
    overflow: hidden;
  }
  &-header {
    background: $bh-blue--1;
    color: $bh-white;
    padding: 8px 16px;
    font-weight: bold;
    font-size: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    &-title {
      flex-grow: 1;
    }
  }
  &-status {
    padding: 4px 16px;
    background-color: $bh-gray--6 !important;
    border-radius: 0 !important;
    color: $bh-gray--1;
  }
  &-list {
    padding: 8px 16px;
    &--item {
      display: flex;
      color: $bh-gray--2;
      align-items: center;
      justify-content: stretch;
      flex-wrap: wrap;
      &-overview {
        flex-grow: 1;
        display: flex;
        align-items: center;
        width: 100%;
      }
      &-details {
        flex-grow: 1;
        transition-duration: 300ms;
        padding-bottom: 5px;
        font-size: 12px;
        span {
          color: $bh-red--dark;
        }
        ul {
          list-style: none;
          color: $bh-red--dark;
          padding-left: 0px !important;
        }
        &-list {
          overflow: auto;
          display: block;
          max-height: 80px;
          span {
            display: inline;
            &:first-child:first-letter {
              text-transform: uppercase;
            }
            &:not(:last-child)::after {
              content: ',';
              display: inline-block;
              width: 10px;
            }
          }
        }
      }
      &-detail {
        display: block;
        color: $bh-red--dark;
      }
      &.failed {
        .v-icon {
        }
      }
      &-name {
        flex-grow: 1;
        font-weight: bold;
        margin-left: 5px;
        line-height: 1;
      }
      &-status {
        line-height: 1;
      }
      .v-btn {
        font-weight: bold;
        margin-left: 8px;
        span {
          font-size: 14px;
        }
      }
    }
  }
}
</style>
